import React, { useState, useEffect, useRef } from 'react';
import './GameBoard.css';

// All special corner positions
const SPECIAL_POSITIONS = [1, 11, 111, 121];

/** Set grouping colors for the board's outer ring **/
const GROUPS = [
  { cells: [1, 11, 111, 121], color: '#FFF2D5' },
  { cells: [2, 3, 4], color: '#9B59B6' },
  { cells: [5, 6, 7], color: '#D35400' },
  { cells: [8, 9, 10], color: '#1ABC9C' },
  { cells: [22, 33, 44], color: '#C0392B' },
  { cells: [55, 66, 77], color: '#F1C40F' },
  { cells: [88, 99, 110], color: '#2980B9' },
  { cells: [120, 119, 118], color: '#E91E63' },
  { cells: [117, 116, 115], color: '#8E44AD' },
  { cells: [114, 113, 112], color: '#27AE60' },
  { cells: [100, 89, 78], color: '#FF5733' },
  { cells: [67, 56, 45], color: '#2E86C1' },
  { cells: [34, 23, 12], color: '#F39C12' },
];

// Outer path cells
const MOVEMENT_PATH = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11,
  22, 33, 44, 55, 66, 77, 88, 99, 110, 121,
  120, 119, 118, 117, 116, 115, 114, 113, 112, 111,
  100, 89, 78, 67, 56, 45, 34, 23, 12,
];

// Middle highlight cells (the “interior”)
const MIDDLE_HIGHLIGHT_CELLS = [
  13, 14, 15, 16, 17, 18, 19, 20, 21,
  24, 25, 26, 27, 28, 29, 30, 31, 32,
  35, 36, 37, 38, 39, 40, 41, 42, 43,
  46, 47, 48, 49, 50, 51, 52, 53, 54,
  57, 58, 59, 60, 61, 62, 63, 64, 65,
  68, 69, 70, 71, 72, 73, 74, 75, 76,
  79, 80, 81, 82, 83, 84, 85, 86, 87,
  90, 91, 92, 93, 94, 95, 96, 97, 98,
  101, 102, 103, 104, 105, 106, 107, 108, 109,
];

function findGroupForCell(cell) {
  return GROUPS.find((g) => g.cells.includes(cell)) || null;
}

function getPathIndex(cell) {
  return MOVEMENT_PATH.indexOf(cell);
}

function getSetLineStyle(cell) {
  const group = findGroupForCell(cell);
  if (!group) return null;

  const idx = getPathIndex(cell);
  if (idx === -1) return null;

  const color = group.color || '#000';
  const thickness = '5px';

  // top row => idx 0..10 => line on bottom
  // right col => idx 11..19 => line on left
  // bottom row => idx 20..30 => line on top
  // left col => idx 31..39 => line on right
  if (idx >= 0 && idx <= 10) {
    return {
      backgroundColor: color,
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      height: thickness,
    };
  } else if (idx >= 11 && idx <= 19) {
    return {
      backgroundColor: color,
      position: 'absolute',
      top: 0,
      left: 0,
      width: thickness,
      height: '100%',
    };
  } else if (idx >= 20 && idx <= 30) {
    return {
      backgroundColor: color,
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: thickness,
    };
  } else {
    return {
      backgroundColor: color,
      position: 'absolute',
      top: 0,
      right: 0,
      width: thickness,
      height: '100%',
    };
  }
}

function getColorForSet(setId, allProps) {
  const prop = allProps.find((p) => p.set_id === setId);
  if (!prop) return '#CCC';
  const group = findGroupForCell(prop.property_id);
  return group ? group.color : '#CCC';
}

function getMultiPlayerTokenStyle(playersOnCell, playerColors) {
  if (!playersOnCell.length) return {};

  if (playersOnCell.length === 1) {
    return {
      backgroundColor: playerColors[playersOnCell[0]] || 'gray',
    };
  } else if (playersOnCell.length === 2) {
    const [p1, p2] = playersOnCell;
    const c1 = playerColors[p1] || 'gray';
    const c2 = playerColors[p2] || 'gray';
    return {
      background: `linear-gradient(to right, ${c1} 50%, ${c2} 50%)`,
    };
  } else {
    // For 3+ players, use a conic gradient
    const slice = 100 / playersOnCell.length;
    const stops = playersOnCell.map((pid, i) => {
      const start = slice * i;
      const end = slice * (i + 1);
      const color = playerColors[pid] || 'gray';
      return `${color} ${start}%, ${color} ${end}%`;
    });
    return {
      background: `conic-gradient(${stops.join(',')})`,
    };
  }
}

/** DiceFace => shows pips for a given dice face (1-6). **/
function DiceFace({ value }) {
  const pipPositions = {
    1: [[50, 50]],
    2: [[30, 30], [70, 70]],
    3: [[30, 30], [50, 50], [70, 70]],
    4: [[30, 30], [70, 30], [30, 70], [70, 70]],
    5: [[30, 30], [70, 30], [50, 50], [30, 70], [70, 70]],
    6: [[30, 30], [70, 30], [30, 70], [70, 70], [30, 50], [70, 50]],
  };

  const positions = pipPositions[value] || [];
  return (
    <div className="dice-face">
      {positions.map((pos, idx) => (
        <div
          key={idx}
          className="pip"
          style={{ top: `${pos[0]}%`, left: `${pos[1]}%` }}
        />
      ))}
    </div>
  );
}

/** Dice => a single die, which can be "rolling" or "settled". **/
function Dice({ value, isRolling }) {
  return (
    <div className={`dice ${isRolling ? 'rolling' : ''}`}>
      <DiceFace value={value} />
    </div>
  );
}

const GameBoard = ({
  gameState = {},
  playerColors = {},
  currentPlayer,
  diceResult,
  onRollDice,
  properties = [],
  polyBalance,
  opolBalance,
  diceRolls = {},
  walletAddress,
  gameId,
  playerOrder = [],
  onTopUp,
  mintedSets = {},
  turnTimeLeft = 60, // [NEW: add a prop for the time left]
}) => {
  const playerColor = playerColors[walletAddress] || 'gray';

  // Generate cells 1..121
  const cells = Array.from({ length: 121 }, (_, idx) => idx + 1);

  const [canBuyProperty, setCanBuyProperty] = useState(false);
  const [currentProperty, setCurrentProperty] = useState(null);

  // We'll keep track of the final dice faces in diceValues
  const [diceValues, setDiceValues] = useState([]);
  const [isRolling, setIsRolling] = useState(false);

  const [ownedProperties, setOwnedProperties] = useState([]);
  const [ownedSets, setOwnedSets] = useState([]);
  const [showTopUp, setShowTopUp] = useState(false);
  const hasRolledOnce = gameState[walletAddress]?.hasRolledOnce || false;

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const [zoomLevel, setZoomLevel] = useState(isMobile ? 0.4 : 1);

  const boardRef = useRef(null);

  // SWAP states
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [showSwapUI, setShowSwapUI] = useState(false);
  const [offeredPolyStr, setOfferedPolyStr] = useState('');
  const [requestedPolyStr, setRequestedPolyStr] = useState('');
  const [opponentProps, setOpponentProps] = useState([]);
  const [requestedProps, setRequestedProps] = useState([]);
  const [yourProps, setYourProps] = useState([]);
  const [offeredProps, setOfferedProps] = useState([]);

  const otherPlayers = playerOrder
    .filter((addr) => addr !== walletAddress)
    .map((addr) => ({
      wallet: addr,
      color: playerColors[addr] || 'gray',
    }));

  // Collapsible states
  const [isBalanceCollapsed, setIsBalanceCollapsed] = useState(false);
  const [isPropertiesCollapsed, setIsPropertiesCollapsed] = useState(false);

  // Check if user can buy property
  useEffect(() => {
    if (!gameState || !walletAddress) return;
    const position = gameState[walletAddress]?.position;
    const prop = properties.find((p) => p.property_id === position);

    if (prop && !prop.owner_wallet) {
      setCanBuyProperty(true);
      setCurrentProperty(prop);
    } else {
      setCanBuyProperty(false);
      setCurrentProperty(null);
    }
  }, [gameState, properties, walletAddress]);

  // Dice roll animation
  useEffect(() => {
    if (diceResult) {
      setIsRolling(true);
      const rollingInterval = setInterval(() => {
        setDiceValues([
          Math.floor(Math.random() * 6) + 1,
          Math.floor(Math.random() * 6) + 1,
        ]);
      }, 80);
      setTimeout(() => {
        clearInterval(rollingInterval);
        let die1, die2;
        do {
          die1 = Math.floor(Math.random() * 6) + 1;
          die2 = diceResult - die1;
        } while (die2 < 1 || die2 > 6);

        setDiceValues([die1, die2]);
        setIsRolling(false);
      }, 1000);
    }
  }, [diceResult]);

  // Owned properties
  useEffect(() => {
    if (!properties || !walletAddress) return;
    const mine = properties.filter((p) => p.owner_wallet === walletAddress);
    setOwnedProperties(mine);
  }, [properties, walletAddress]);

  // Owned sets
  useEffect(() => {
    if (!properties.length || !walletAddress) {
      setOwnedSets([]);
      return;
    }
    const setsMap = {};
    for (const p of properties) {
      if (!p.set_id) continue;
      if (!setsMap[p.set_id]) {
        setsMap[p.set_id] = { totalCount: 0, ownedCount: 0 };
      }
      setsMap[p.set_id].totalCount++;
      if (p.owner_wallet === walletAddress) {
        setsMap[p.set_id].ownedCount++;
      }
    }
    const fullSets = [];
    for (const setId in setsMap) {
      const info = setsMap[setId];
      if (info.ownedCount === info.totalCount && info.totalCount > 0) {
        fullSets.push(Number(setId));
      }
    }
    setOwnedSets(fullSets);
  }, [properties, walletAddress]);

  // Show top-up if special corner
  useEffect(() => {
    if (!walletAddress || !gameState[walletAddress]) return;
    const position = gameState[walletAddress].position;
    if (SPECIAL_POSITIONS.includes(position)) {
      const prop = properties.find((p) => p.property_id === position);
      if (prop && prop.owner_wallet === walletAddress) {
        setShowTopUp(true);
      } else {
        setShowTopUp(false);
      }
    } else {
      setShowTopUp(false);
    }
  }, [gameState, properties, walletAddress]);

  // Precompute user’s props
  useEffect(() => {
    const userProps = properties.filter((p) => p.owner_wallet === walletAddress);
    setYourProps(userProps);
  }, [properties, walletAddress]);

  // If user picks opponent
  useEffect(() => {
    if (!selectedPlayer) {
      setOpponentProps([]);
      setRequestedProps([]);
      return;
    }
    const oppProps = properties.filter((p) => p.owner_wallet === selectedPlayer);
    setOpponentProps(oppProps);
    setRequestedProps([]);
  }, [selectedPlayer, properties]);

  // Auto-center on your cell
  useEffect(() => {
    if (!boardRef.current) return;
    const myPos = gameState[walletAddress]?.position;
    if (!myPos) return;
    const cellEl = boardRef.current.querySelector(`[data-cell-id="${myPos}"]`);
    if (cellEl) {
      const container = boardRef.current;
      const containerRect = container.getBoundingClientRect();
      const cellRect = cellEl.getBoundingClientRect();
      const offsetLeft = cellRect.left - containerRect.left + container.scrollLeft;
      const offsetTop = cellRect.top - containerRect.top + container.scrollTop;
      const centerX = offsetLeft - containerRect.width / 2 + cellRect.width / 2;
      const centerY = offsetTop - containerRect.height / 2 + cellRect.height / 2;
      container.scrollTo({
        left: centerX,
        top: centerY,
        behavior: 'smooth',
      });
    }
  }, [gameState, walletAddress]);

  // Buy property
  const buyProperty = () => {
    if (currentProperty && walletAddress) {
      window.socket.emit('buy-property', {
        gameId,
        propertyId: currentProperty.property_id,
        walletAddress,
      });
    }
  };

  // Trigger top-up
  const handleTopUpLocal = () => {
    onTopUp && onTopUp();
  };

  // Start swap
  const handlePropertySwap = () => {
    setShowSwapUI(true);
  };

  const handleYourPropsChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions);
    const selectedIds = selectedOptions.map((opt) => parseInt(opt.value, 10));
    setOfferedProps(selectedIds);
  };

  const handleOpponentPropsChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions);
    const selectedIds = selectedOptions.map((opt) => parseInt(opt.value, 10));
    setRequestedProps(selectedIds);
  };

  const doSwapRequest = () => {
    if (!selectedPlayer) {
      alert('Please select a player to swap with.');
      return;
    }
    const offeredPoly = offeredPolyStr ? parseInt(offeredPolyStr, 10) : 0;
    const requestedPoly = requestedPolyStr ? parseInt(requestedPolyStr, 10) : 0;

    window.socket.emit('swap-request', {
      gameId,
      fromPlayer: walletAddress,
      toPlayer: selectedPlayer,
      offeredProperties: offeredProps,
      requestedProperties: requestedProps,
      offeredPoly,
      requestedPoly,
    });
    setShowSwapUI(false);
  };

  const mintSet = (setId) => {
    window.socket.emit('mint-set', { gameId, setId, walletAddress });
  };

  const handleZoomIn = () => setZoomLevel((z) => Math.min(z + 0.1, 1.5));
  const handleZoomOut = () => setZoomLevel((z) => Math.max(z - 0.1, 0.4));

  return (
    <div className="game-container">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <img src="/logo.svg" alt="Polyland Logo" className="game-logo" />
        </div>
        <div className="header-legend">
          <h3>Players</h3>
          <div className="legend-items">
            {Object.keys(playerColors).map((playerId) => {
              const displayId = playerId.startsWith('0x')
                ? playerId.slice(0, 6) + '...'
                : playerId;
              return (
                <div key={playerId} className="legend-item">
                  <span
                    className="token"
                    style={{ backgroundColor: playerColors[playerId] || 'gray' }}
                  />
                  {displayId} (Rolled: {diceRolls[playerId] || '-'})
                </div>
              );
            })}
          </div>
        </div>
      </header>

      {/* Turn Timer Display */}
      <div className="turn-timer-display">
        <div className="turn-info">
          Turn: {currentPlayer?.slice(0, 6) || '...'}
        </div>
        <div className="timer-info">Time Left: {turnTimeLeft}s</div>
      </div>

      {/* Game Actions Bar */}
      <div className="game-actions-bar">
        <button
          onClick={buyProperty}
          disabled={!canBuyProperty || currentPlayer !== walletAddress || !hasRolledOnce}
          className={`buy-property-btn ${
            canBuyProperty && currentPlayer === walletAddress ? 'enabled' : 'disabled'
          }`}
        >
          <div className="buy-icon" />
          <div className="buy-text">Buy</div>
        </button>

        <button
          onClick={onRollDice}
          disabled={currentPlayer !== walletAddress || playerOrder.length < 2}
          className={`pushable roll-button ${
            currentPlayer === walletAddress ? 'enabled' : 'disabled'
          }`}
        >
          <span className="front">ROLL</span>
        </button>

        <button
          onClick={handlePropertySwap}
          disabled={currentPlayer !== walletAddress || !hasRolledOnce}
          className={`swap-property-btn ${
            currentPlayer !== walletAddress || !hasRolledOnce ? 'disabled' : ''
          }`}
        >
          <div className="swap-icon" />
          <div className="swap-text">Swap</div>
        </button>
      </div>

      {/* Zoom controls */}
      <div className="zoom-controls">
        <button onClick={handleZoomOut}>-</button>
        <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
        <button onClick={handleZoomIn}>+</button>
      </div>

      {/* Main layout: left column, board center, right column */}
      <div className="board-layout">
        {/* Left column */}
        <div className="left-column">
          {/* Balance Box */}
          <div className="balance-box">
            <h3
              style={{ cursor: 'pointer' }}
              onClick={() => setIsBalanceCollapsed((prev) => !prev)}
            >
              Your Balance
              <span style={{ marginLeft: '8px' }}>{isBalanceCollapsed ? '+' : '-'}</span>
            </h3>
            {!isBalanceCollapsed && (
              <>
                <div
                  className="player-color-circle"
                  style={{ backgroundColor: playerColor }}
                />
                <p>Game Balance: {polyBalance} POLY</p>
                <p>Wallet Balance: {opolBalance} OPOV</p>
                <p>Dice Roll: {diceResult || '-'}</p>
              </>
            )}
          </div>

          {/* Owned Properties Box */}
          <div className="owned-properties-box">
            <h3
              style={{ cursor: 'pointer' }}
              onClick={() => setIsPropertiesCollapsed((prev) => !prev)}
            >
              Your Properties
              <span style={{ marginLeft: '8px' }}>{isPropertiesCollapsed ? '+' : '-'}</span>
            </h3>
            {!isPropertiesCollapsed && (
              <>
                {ownedProperties.length === 0 ? (
                  <p>You currently own no properties.</p>
                ) : (
                  <ul>
                    {ownedProperties.map((prop) => (
                      <li key={prop.property_id}>
                        {prop.name} ({prop.value} POLY)
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>

        {/* Board Container */}
        <div className="board-container" ref={boardRef}>
          <div
            className="board"
            style={{
              transform: `scale(${zoomLevel})`,
              transformOrigin: 'top left',
            }}
          >
            {cells.map((cell) => {
              const isOuterCell = MOVEMENT_PATH.includes(cell);
              const property = properties.find((p) => p.property_id === cell);
              const playersOnCell = Object.keys(gameState).filter(
                (playerId) => gameState[playerId]?.position === cell
              );
              const isMiddleHighlight = MIDDLE_HIGHLIGHT_CELLS.includes(cell);
              const setLineStyle = getSetLineStyle(cell);

              return (
                <div
                  key={cell}
                  className={`cell ${isOuterCell && playersOnCell.length > 0 ? 'highlight' : ''} ${
                    isMiddleHighlight ? 'middle-highlight' : ''
                  }`}
                  data-cell-id={cell}
                >
                  <div className="cell-content">
                    {isOuterCell && property && (
                      <>
                        <span className="cell-name">{property.name}</span>
                        <div className="property-value">{property.value} POLY</div>
                        {property.owner_wallet && (
                          <div className="ownership">
                            <span
                              className="owner-circle"
                              style={{
                                backgroundColor:
                                  playerColors[property.owner_wallet] || 'gray',
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}
                    {isOuterCell && !property && (
                      <span className="cell-name">Unnamed</span>
                    )}

                    {playersOnCell.length > 0 && (
                      <span
                        className="token"
                        style={getMultiPlayerTokenStyle(playersOnCell, playerColors)}
                        title={`Players here: ${playersOnCell.join(', ')}`}
                      />
                    )}

                    {cell === 61 && diceValues.length > 0 && (
                      <div className="dice-container">
                        <Dice value={diceValues[0]} isRolling={isRolling} />
                        <Dice value={diceValues[1]} isRolling={isRolling} />
                      </div>
                    )}
                    {setLineStyle && <div style={setLineStyle} />}
                  </div>
                </div>
              );
            })}
            {/* Middle overlay: this div covers the interior of the board with a single background image */}
            <div className="middle-overlay" />
          </div>
        </div>

        {/* Right column: Mintable sets */}
        <div className="right-column">
          <div className="mint-sets-container">
            <h3>Mintable Sets</h3>
            {ownedSets.length === 0 ? (
              <p>No sets available to mint.</p>
            ) : (
              ownedSets.map((setId) => {
                const color = getColorForSet(setId, properties) || '#ccc';
                const isAlreadyMinted = Boolean(mintedSets[setId]);
                return (
                  <button
                    key={setId}
                    onClick={() => mintSet(setId)}
                    disabled={isAlreadyMinted}
                    style={{
                      backgroundColor: color,
                      color: '#fff',
                      border: 'none',
                      marginBottom: '5px',
                      opacity: isAlreadyMinted ? 0.5 : 1,
                      cursor: isAlreadyMinted ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isAlreadyMinted ? `Set #${setId} Minted` : `Mint Set #${setId}`}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Top-up Modal */}
      {showTopUp && (
        <div className="top-up-modal">
          <h3>Top Up Your Balance</h3>
          <p>You landed on a special top-up property you own!</p>
          <button onClick={handleTopUpLocal} className="btn btn-primary">
            Top Up 1500 POLY
          </button>
          <button onClick={() => setShowTopUp(false)} className="btn btn-secondary">
            Cancel
          </button>
        </div>
      )}

      {/* Swap Modal */}
      {showSwapUI && (
        <div className="modal-overlay">
          <div className="modal-content modern">
            <h3>Initiate a Property Swap</h3>
            <form className="swap-form">
              <div className="form-group">
                <label>Select a player to swap with:</label>
                <select
                  value={selectedPlayer}
                  onChange={(e) => setSelectedPlayer(e.target.value)}
                  className="form-control"
                >
                  <option value="">-- Select Player --</option>
                  {otherPlayers.map(({ wallet, color }) => (
                    <option key={wallet} value={wallet}>
                      [{color}] {wallet.slice(0, 6)}…
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Your Properties (multi-select):</label>
                <select
                  multiple
                  className="form-control"
                  style={{ height: '120px' }}
                  value={offeredProps.map(String)}
                  onChange={handleYourPropsChange}
                >
                  {yourProps.map((prop) => (
                    <option key={prop.property_id} value={prop.property_id}>
                      {prop.name} (#{prop.property_id})
                    </option>
                  ))}
                </select>
                <small>Hold Ctrl (or Cmd) to select multiple</small>
              </div>

              <div className="form-group">
                <label>Opponent’s Properties (multi-select):</label>
                <select
                  multiple
                  className="form-control"
                  style={{ height: '120px' }}
                  value={requestedProps.map(String)}
                  onChange={handleOpponentPropsChange}
                >
                  {opponentProps.map((prop) => (
                    <option key={prop.property_id} value={prop.property_id}>
                      {prop.name} (#{prop.property_id})
                    </option>
                  ))}
                </select>
                <small>Hold Ctrl (or Cmd) to select multiple</small>
              </div>

              <div className="form-row">
                <div className="form-group half">
                  <label>POLY you offer:</label>
                  <input
                    type="number"
                    value={offeredPolyStr}
                    onChange={(e) => setOfferedPolyStr(e.target.value)}
                    className="form-control"
                  />
                </div>
                <div className="form-group half">
                  <label>POLY you request:</label>
                  <input
                    type="number"
                    value={requestedPolyStr}
                    onChange={(e) => setRequestedPolyStr(e.target.value)}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="modal-buttons">
                <button type="button" onClick={doSwapRequest} className="btn btn-primary">
                  Send Swap Request
                </button>
                <button
                  type="button"
                  onClick={() => setShowSwapUI(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameBoard;
